import { Component, type ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@itaas/core/auth/useAuth";
import { loadStoredSession } from "@itaas/core/auth/auth.storage";
import { hasPermission } from "@itaas/core/rbac/access";
import {
  ProductShowcase,
  resolvePepApiBase,
  resolvePepWebBase,
} from "@itaas/core/marketing";
import { getLicensedModulesFromSession } from "@/auth/suite-auth.service";
import { resolveModuleEditionLabels } from "@itaas/core/editions/module-edition-label";
import { getAccessibleModulesForSession, navigateToModule } from "@/navigation/post-login";
import { SuiteAuthShell } from "@/components/SuiteAuthShell";
import { routes } from "@/router/all_routes";
import { canAccessPlatformHub } from "@/navigation/platform-access";
import { visiblePlatformMenuItems } from "@/navigation/platform-menu.config";

const PEP_URL = resolvePepWebBase();

function safeLicensedModules(): string[] {
  try {
    const session = loadStoredSession();
    return session ? getLicensedModulesFromSession(session) : [];
  } catch {
    return [];
  }
}

function safeAccessibleCount(): number {
  try {
    return getAccessibleModulesForSession(loadStoredSession()).length;
  } catch {
    return 0;
  }
}

function safeShowCommercialAdmin(user: ReturnType<typeof useAuth>["user"]): boolean {
  try {
    return Boolean(
      user?.isSuperAdmin || (user && hasPermission(user, "pep.leads.read")),
    );
  } catch {
    return false;
  }
}

export function ModuleLauncherPage() {
  const { user, tenant, logout, verifySession, isLoading } = useAuth();
  const [ready, setReady] = useState(false);
  const [renderError, setRenderError] = useState<string | null>(null);
  const pepApiBase = useMemo(() => {
    try {
      return resolvePepApiBase();
    } catch {
      return undefined;
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    void verifySession()
      .catch(() => undefined)
      .finally(() => {
        if (mounted) setReady(true);
      });
    return () => {
      mounted = false;
    };
  }, [verifySession]);

  const moduleEditionLabels = useMemo(() => {
    if (!ready) return {} as Record<string, string>;
    try {
      const session = loadStoredSession();
      return resolveModuleEditionLabels(session?.moduleEditions);
    } catch {
      return {};
    }
  }, [ready, user]);

  const licensedModuleIds = useMemo(() => {
    if (!ready) return [] as string[];
    return safeLicensedModules();
  }, [ready, user]);

  const accessibleCount = useMemo(() => {
    if (!ready) return 0;
    return safeAccessibleCount();
  }, [ready, user]);

  const showPlatformHub = useMemo(
    () => safeShowCommercialAdmin(user) || (user && canAccessPlatformHub(user)),
    [user],
  );

  const platformMenuItems = useMemo(
    () => (user ? visiblePlatformMenuItems(user) : []),
    [user],
  );

  if (isLoading || !ready) {
    return (
      <div className="suite-center-message">
        <p className="text-muted mb-0">Chargement de vos modules…</p>
      </div>
    );
  }

  if (renderError) {
    return (
      <div className="suite-center-message">
        <div className="alert alert-danger">{renderError}</div>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => window.location.reload()}
        >
          Recharger
        </button>
      </div>
    );
  }

  const displayName =
    user?.displayName?.trim() ||
    [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim() ||
    user?.email ||
    "Utilisateur";

  return (
    <SuiteAuthShell
      layout="modules"
      showFormBrand={false}
      heroTitle={
        <>
          Bienvenue,
          <br />
          {displayName.split(" ")[0]}.
        </>
      }
      heroSubtitle={
        tenant?.name
          ? `Organisation : ${tenant.name}`
          : "Sélectionnez un module pour continuer."
      }
      heroExtra={
        <div className="suite-modules-hero-meta">
          <div className="suite-modules-hero-stat">
            <strong>{accessibleCount || licensedModuleIds.length || "—"}</strong>
            <span>module{accessibleCount > 1 ? "s" : ""} accessible{accessibleCount > 1 ? "s" : ""}</span>
          </div>
          {user?.email ? (
            <p className="suite-modules-hero-email mb-0">{user.email}</p>
          ) : null}
          <div className="suite-modules-hero-actions">
            <a
              href={PEP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm btn-light"
            >
              Tous les produits
            </a>
            <button
              type="button"
              className="btn btn-sm btn-outline-light"
              onClick={() => logout()}
            >
              Déconnexion
            </button>
          </div>
        </div>
      }
      footer={
        <p className="suite-auth-footer mb-0">
          <a href={PEP_URL} target="_blank" rel="noopener noreferrer">
            Documentation, tarifs & essais
          </a>
        </p>
      }
    >
      <div className="suite-modules-content">
        {accessibleCount === 0 ? (
          <div className="alert alert-warning mb-4">
            Aucun module accessible pour votre profil. Contactez l&apos;administrateur
            de votre organisation ou découvrez nos produits ci-dessous.
          </div>
        ) : null}

        <ShowcaseBoundary onError={setRenderError}>
          <ProductShowcase
            licensedModuleIds={licensedModuleIds}
            moduleEditionLabels={moduleEditionLabels}
            pepBaseUrl={PEP_URL}
            pepApiBaseUrl={pepApiBase}
            userEmail={user?.email}
            organizationName={tenant?.name}
            onOpenModule={navigateToModule}
            utmSource="suite_launcher"
            showHero={false}
          />
        </ShowcaseBoundary>

        {showPlatformHub && platformMenuItems.length > 0 && (
          <section className="suite-platform-section mt-4 pt-4 border-top">
            <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
              <h2 className="itaas-mkt-section-title mb-0">Plateforme ITaaS</h2>
              <Link to={routes.platform} className="btn btn-sm btn-outline-primary">
                Ouvrir le hub
              </Link>
            </div>
            <div className="row g-2">
              {platformMenuItems.map((item) => (
                <div className="col-md-6" key={item.id}>
                  <Link to={item.path} className="itaas-mkt-card text-decoration-none d-block">
                    <span className="itaas-mkt-card-title">{item.label}</span>
                    <span className="itaas-mkt-card-tagline">{item.description}</span>
                  </Link>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </SuiteAuthShell>
  );
}

class ShowcaseBoundary extends Component<
  { children: ReactNode; onError: (message: string) => void },
  { hasError: boolean; message: string }
> {
  state = { hasError: false, message: "" };

  static getDerivedStateFromError(error: Error): { hasError: boolean; message: string } {
    return {
      hasError: true,
      message: error.message || "Impossible d'afficher le catalogue produits.",
    };
  }

  componentDidCatch(error: Error): void {
    this.props.onError(error.message || "Impossible d'afficher le catalogue produits.");
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="alert alert-warning">
          {this.state.message}
        </div>
      );
    }
    return this.props.children;
  }
}
